import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import { IUserTasksCapacity } from '../models/user-tasks-capacity.model';
import { UserTasksCapacityApiClient } from './api/user-tasks-capacity.api-client';
import { IUserTasksCapacityService } from './i-user-tasks-capacity.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class UserTasksCapacityService implements IUserTasksCapacityService {
  public readonly capacity$ = new BehaviorSubject<IUserTasksCapacity>(null);
  private initialized = false;

  public constructor(
    private readonly apiClient: UserTasksCapacityApiClient,
    private readonly signalRHub: NimbusSignalRHub,
    private readonly authenticationService: IAuthenticationService,
  ) {}

  public initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAuthenticated) => (isAuthenticated ? this.signalRHub.onUserCapacityChanged$ : EMPTY)),
        tap((data) => this.capacity$.next(data)),
        untilDestroyed(this),
      )
      .subscribe();

    this.authenticationService.isAuthenticated$
      .pipe(
        distinctUntilChanged(),
        switchMap((isAuthenticated) =>
          isAuthenticated
            ? this.signalRHub.isConnected$.pipe(
                distinctUntilChanged(),
                filter((isConnected) => isConnected),
                switchMap(() => {
                  const userId = this.authenticationService.getUserInfo()?.userId;
                  if (!userId) {
                    return EMPTY;
                  }
                  return this.apiClient.getUserTasksCapacity(userId).pipe(catchError(() => EMPTY));
                }),
              )
            : EMPTY,
        ),
        tap((data) => this.capacity$.next(data)),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
